import React from 'react';
import ProgressIndicator from '../../../components/ui/ProgressIndicator';
import Icon from '../../../components/AppIcon';

const SkillsProgress = ({ skills, onProgressClick }) => {
  const getSkillIcon = (skillName) => {
    const iconMap = {
      'Clarity': 'MessageSquare',
      'Confidence': 'Zap',
      'Control': 'Shield',
      'Close': 'Target'
    };
    return iconMap?.[skillName] || 'BarChart3';
  };

  const getSkillColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'primary';
    if (score >= 40) return 'warning';
    return 'error';
  };

  const averageScore = skills?.length > 0 
    ? Math.round(skills?.reduce((sum, skill) => sum + skill?.score, 0) / skills?.length)
    : 0;

  // Show empty state if no skills data
  if (!skills || skills.length === 0) {
    return (
      <div className="bg-card rounded-2xl p-6 shadow-card border border-border">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Skills Progress</h2>
            <p className="text-sm text-muted-foreground">Your coaching metrics</p>
          </div>
        </div>
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
            <Icon name="Target" size={32} color="var(--color-muted-foreground)" />
          </div>
          <h3 className="text-base font-medium text-foreground mb-2">Start Your Journey</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Complete your first drills to see your skills progress here
          </p>
          <button
            onClick={onProgressClick}
            className="text-sm font-medium text-primary hover:underline"
          >
            Practice Now →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-2xl p-6 shadow-card border border-border">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={24} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground">Skills Progress</h2>
            <p className="text-sm text-muted-foreground">Your coaching metrics</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-foreground">{averageScore}</div>
          <div className="text-xs text-muted-foreground">Avg Score</div>
        </div>
      </div>
      <div className="space-y-5">
        {skills?.map((skill) => (
          <div key={skill?.id}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Icon name={getSkillIcon(skill?.name)} size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm font-medium text-foreground">{skill?.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">{skill?.score}</span>
                {skill?.trend > 0 && (
                  <div className="flex items-center gap-1 text-success">
                    <Icon name="TrendingUp" size={14} />
                    <span className="text-xs font-medium">+{skill?.trend}</span>
                  </div>
                )}
                {skill?.trend < 0 && (
                  <div className="flex items-center gap-1 text-error">
                    <Icon name="TrendingDown" size={14} />
                    <span className="text-xs font-medium">{skill?.trend}</span>
                  </div>
                )}
              </div>
            </div>
            <ProgressIndicator
              value={skill?.score}
              max={100}
              showPercentage={false}
              color={getSkillColor(skill?.score)}
              size="default"
            />
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 rounded-lg bg-muted/50 border border-border">
        <div className="flex items-start gap-3 mb-3">
          <Icon name="Lightbulb" size={20} color="var(--color-warning)" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">Coach's Tip</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Focus on improving your lowest scoring skill. Consistent practice in weak areas leads to the biggest performance gains.
            </p>
          </div>
        </div>
        {onProgressClick && (
          <button
            onClick={onProgressClick}
            className="w-full mt-2 py-2 px-4 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-lg text-sm font-medium text-primary transition-all"
          >
            📊 See Your Detailed Progress
          </button>
        )}
      </div>
    </div>
  );
};

export default SkillsProgress;